import { motion } from "framer-motion";
import { Check } from "lucide-react";

const LAUNCH_FEATURES = [
  "Student Progress Dashboard",
  "Students and complete learning records",
  "Student Packages",
  "Instructor Insight",
  "Professional branded PDF reports",
  "Structured RSA and DVSA assessments",
];

const PRO_FEATURES = [
  "Student Progress Dashboard",
  "Students and Student Packages",
  "Instructor Insight",
  "Branded assessment and Student Progress reports",
  "Structured RSA and DVSA assessments",
  "Cancel anytime",
];

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const FeatureItem = ({ children, accent }) => (
  <li className="flex items-start gap-3">
    <span
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
        accent ? "bg-[#00DC82]/20 text-[#00DC82]" : "bg-white/[0.06] text-[#00DC82]"
      }`}
    >
      <Check size={13} strokeWidth={3} />
    </span>
    <span className="text-sm text-slate-300 leading-relaxed">{children}</span>
  </li>
);

export const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative overflow-hidden scroll-mt-24"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[420px] w-[700px] rounded-full bg-[#00DC82]/[0.05] blur-[150px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center flex flex-col gap-5"
        >
          <h2
            data-testid="pricing-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
          >
            Professional tools. Clear pricing.
          </h2>
          <p
            data-testid="pricing-subtitle"
            className="text-base md:text-lg text-slate-400 leading-relaxed"
          >
            Manage student progress with MockTP Professional. Available now on Google
            Play; iOS is coming soon.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-start">
          {/* Launch status — highlighted */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid="pricing-card-launch"
            className="relative flex flex-col gap-7 rounded-2xl border border-[#00DC82]/40 bg-[#00DC82]/[0.05] p-8 shadow-[0_30px_80px_-30px_rgba(0,220,130,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5"
          >
            <span className="absolute -top-3 left-8 rounded-full bg-[#00DC82] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#080C16]">
              Student Progress Management
            </span>

            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold tracking-tight text-white">
                Built for your lessons
              </h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-5xl font-bold tracking-tighter text-white">MockTP</span>
              </div>
            </div>

            <ul className="flex flex-col gap-3.5">
              {LAUNCH_FEATURES.map((f) => (
                <FeatureItem key={f} accent>
                  {f}
                </FeatureItem>
              ))}
            </ul>

            <a
              href="https://play.google.com/store/apps/details?id=com.mocktp.app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-launch-status"
              className="mt-auto h-12 rounded-full bg-[#00DC82] text-[#080C16] font-semibold flex items-center justify-center"
            >
              Available now on Google Play
            </a>
          </motion.div>

          {/* MockTP Pro */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid="pricing-card-pro"
            className="relative flex flex-col gap-7 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.7)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-white/15"
          >
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold tracking-tight text-white">
                MockTP Professional
              </h3>
              <span className="mt-2 w-fit rounded-full border border-[#00DC82]/25 bg-[#00DC82]/[0.08] px-3 py-1 text-xs font-semibold text-[#00DC82]">
                30-day free trial
              </span>
              <div className="mt-3 flex flex-col gap-4">
                <div>
                  <p className="text-sm text-slate-400">Ireland</p>
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <span className="text-5xl font-bold tracking-tighter text-white">€3.99</span>
                    <span className="text-base text-slate-400 font-medium">/month</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Great Britain</p>
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <span className="text-5xl font-bold tracking-tighter text-white">£3.49</span>
                    <span className="text-base text-slate-400 font-medium">/month</span>
                  </div>
                </div>
              </div>
            </div>

            <ul className="flex flex-col gap-3.5">
              {PRO_FEATURES.map((f) => (
                <FeatureItem key={f}>{f}</FeatureItem>
              ))}
            </ul>

            <div className="mt-auto flex flex-col gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.mocktp.app"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-pro-status"
                className="h-12 rounded-full border border-white/15 bg-white/[0.04] text-white font-semibold flex items-center justify-center"
              >
                Available now on Google Play
              </a>
              <p className="text-center text-xs text-slate-500">
                iOS coming soon.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
